import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TDS_SECTIONS, findTdsSection } from '@fint/validation';
import { add, currencyMeta, format, formatHuman, formatRateHuman, fromBase, impliedRate, parse, parseRate, subtract, zero, type Money as MoneyValue } from '@fint/money';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { formatDate } from '@/lib/dates';
import { Button, Input, Money, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader } from '@/features/common/ui';
import { invoiceRef } from '@/features/invoices/imported';

/**
 * A deposit that is already in the bank, matched to the invoice it paid.
 *
 * ── THE CASE ──
 * The statement was imported first, so the money is a counted deposit filed
 * as Services; the invoice came later - recreated from the old system, or
 * raised after the advance arrived. Recording a payment the ordinary way
 * would write a SECOND bank row and count the money twice; leaving the
 * invoice unpaid ages it into "Overdue" and shows a receivable nobody owes.
 * After the first migration that was thirteen deposits and ₹2.6 lakh
 * counted on both sides.
 *
 * ── WHAT IT DOES ──
 * The same settlement an AI agent may PROPOSE into the Inbox, done by the
 * person directly - they are the confirmation. The payment is for the whole
 * deposit, the invoice takes the smaller of that and its balance, and what
 * the customer withheld (TDS, the bank's charge) settles the invoice beside
 * the cash, exactly as the Payments form records it. The deposit leaves the
 * "received without an invoice" list because it now has one.
 *
 * ── WHICH INVOICES ARE OFFERED ──
 * The customer's open invoices when the deposit knows its customer; every
 * open invoice otherwise, the ones whose balance equals the deposit first.
 * The person picks - a match on amount alone is a guess, and a deposit that
 * fits two invoices to the rupee (two instalments of the same size) is
 * exactly where a guess goes wrong.
 */

export interface DepositToMatch {
  id: string;
  date: string;
  amount: string;
  currency: string;
  description: string | null;
  reference: string | null;
  partyId?: string | null;
  partyName?: string | null;
}

interface OpenInvoiceRow {
  id: string;
  number: string;
  externalNumber?: string | null;
  customerName?: string;
  partyId?: string;
  date: string;
  balanceDue: string;
  currency: string;
  status?: string;
}

function safeParse(value: string, currency: string): MoneyValue {
  try {
    return parse(value || '0', currency);
  } catch {
    return zero(currency);
  }
}

export function MatchToInvoiceSheet({ deposit, onClose }: { deposit: DepositToMatch; onClose: () => void }) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const [invoiceId, setInvoiceId] = useState('');
  const [tdsSection, setTdsSection] = useState('');
  const [tdsRate, setTdsRate] = useState('');
  const [tdsAmount, setTdsAmount] = useState('');
  const [tan, setTan] = useState('');
  const [bankCharges, setBankCharges] = useState('');
  // The deposit's customer may be wrong, or the invoice may be under a
  // differently spelt customer: one tick widens the list to every open invoice.
  const [everyInvoice, setEveryInvoice] = useState(false);
  /**
   * ── A ₹ DEPOSIT AGAINST A £ INVOICE ──
   * The deposit is the rupees the bank credited, a fact. The invoice is
   * owed in pounds. So the settlement needs what the customer paid in
   * pounds - from the remittance advice - or the rate the bank applied,
   * from which the pounds follow. The amount rule then runs in pounds with
   * that figure standing in for the deposit.
   */
  const [received, setReceived] = useState('');
  const [typedRate, setTypedRate] = useState('');

  // Every open invoice in the book (issued, with a balance). The list is
  // capped at 200 - a book with more open invoices than that has a bigger
  // problem than this sheet.
  const invoices = useQuery({
    queryKey: ['invoices', org?.id, 'unpaid', 'for-matching'],
    queryFn: () => api.get<{ items: OpenInvoiceRow[] }>('/invoices?status=unpaid&limit=200'),
    enabled: !!org?.id,
  });

  const depositMoney = safeParse(deposit.amount, deposit.currency);
  const candidates = useMemo(() => {
    const all = (invoices.data?.items ?? []).filter((i) => safeParse(i.balanceDue, i.currency).minor > 0);
    const own = deposit.partyId && !everyInvoice ? all.filter((i) => i.partyId === deposit.partyId) : all;
    const rank = (i: OpenInvoiceRow) => (safeParse(i.balanceDue, i.currency).minor === depositMoney.minor ? 0 : 1);
    return [...own].sort((a, b) => rank(a) - rank(b) || b.date.localeCompare(a.date));
  }, [invoices.data, deposit.partyId, depositMoney.minor, everyInvoice]);

  const chosen = candidates.find((i) => i.id === invoiceId) ?? null;
  // Every figure below is in the INVOICE's currency; the deposit's own figure is shown as it is.
  const currency = chosen?.currency ?? deposit.currency;
  const cross = !!chosen && chosen.currency !== deposit.currency;
  const rateTyped = parseRate(typedRate);
  const receivedTyped = safeParse(received, currency);
  const cash: MoneyValue = !cross
    ? depositMoney
    : receivedTyped.minor > 0
      ? receivedTyped
      : rateTyped
        ? fromBase(depositMoney, currency, rateTyped)
        : zero(currency);
  const impliedFx = cross && cash.minor > 0 ? impliedRate(depositMoney, cash) : undefined;
  const balance = chosen ? safeParse(chosen.balanceDue, currency) : zero(currency);
  const withheld = add(safeParse(tdsAmount, currency), safeParse(bankCharges, currency));
  const settleable = add(cash, withheld);
  const applied = chosen ? (settleable.minor < balance.minor ? settleable : balance) : zero(currency);
  const balanceAfter = chosen ? subtract(balance, applied) : zero(currency);
  const credit = subtract(settleable, applied);
  // Most of the deposit going to credit is the signature of the wrong
  // invoice: ₹30,000 against an invoice with ₹0.30 left settles the paise
  // and parks the rest. Said loudly, before Settle, not discovered in a
  // report.
  const mostlyCredit = chosen && cash.minor > 0 && credit.minor * 2 > cash.minor;
  const tdsIncomplete = safeParse(tdsAmount, currency).minor > 0 && !tdsSection;
  const receivedMissing = cross && cash.minor <= 0;

  const settle = useMutation({
    mutationFn: () =>
      api.post('/payments/settle', {
        invoiceId,
        depositId: deposit.id,
        // What the customer paid in the invoice's currency; the deposit is the base cash.
        ...(cross ? { received: format(cash) } : {}),
        ...(safeParse(bankCharges, currency).minor > 0 ? { bankCharges } : {}),
        ...(safeParse(tdsAmount, currency).minor > 0
          ? { tds: { section: tdsSection, rate: Number(tdsRate), amount: tdsAmount, ...(tan ? { deductorTan: tan } : {}) } }
          : {}),
      }),
    onSuccess: () => {
      for (const key of ['payments', 'payments-direct', 'invoices', 'invoice', 'invoice-document', 'invoice-payments', 'banking', 'overview', 'dashboard', 'reports']) {
        void queryClient.invalidateQueries({ queryKey: [key, org?.id] });
      }
      onClose();
    },
  });
  const error = settle.error instanceof ApiError ? settle.error : undefined;
  const suggestedTds = findTdsSection(tdsSection)?.rate;

  return (
    <Sheet label="Match to invoice" onClose={onClose} wide testId="match-to-invoice">
      <SheetHeader title="Match to invoice" onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          if (invoiceId) settle.mutate();
        }}
      >
        <SheetBody>
          <div className="rounded-md bg-muted/40 p-3 text-sm" data-testid="match-deposit">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium">{deposit.partyName?.trim() || deposit.description || deposit.reference || 'Deposit'}</span>
              <Money value={deposit.amount} currency={deposit.currency} className="font-semibold" />
            </div>
            <p className="text-xs text-muted-foreground">
              {formatDate(deposit.date)}
              {deposit.partyName?.trim() && deposit.description ? ` · ${deposit.description}` : ''}
            </p>
          </div>

          <label className="block text-sm">
            <span className="mb-1.5 block font-medium">The invoice this paid</span>
            <select
              required
              className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base sm:min-h-9 sm:text-sm"
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              data-testid="match-invoice"
            >
              <option value="">{invoices.isLoading ? 'Loading open invoices…' : candidates.length ? 'Choose an invoice…' : 'No open invoices'}</option>
              {candidates.map((i) => (
                <option key={i.id} value={i.id}>
                  {invoiceRef(i.number, i.externalNumber)}
                  {i.customerName ? ` · ${i.customerName}` : ''} · {formatDate(i.date)} · {i.balanceDue} due
                </option>
              ))}
            </select>
            {deposit.partyId ? (
              <span className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" className="size-4" checked={everyInvoice} onChange={(e) => setEveryInvoice(e.target.checked)} data-testid="match-every-invoice" />
                {candidates.length === 0 && !everyInvoice && !invoices.isLoading ? 'Nothing open for this customer - ' : ''}show every open invoice
              </span>
            ) : null}
          </label>

          {cross && chosen ? (
            <div className="rounded-md border border-border p-3" data-testid="match-fx">
              <p className="text-sm font-medium">
                {chosen.number} is in {currencyMeta(currency).name}; this deposit is {formatHuman(depositMoney)}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Say what the customer paid in {currency} - the remittance advice has it - or the rate the bank applied; the other follows.
              </p>
              <div className="mt-3 grid gap-3 [&>*]:min-w-0 sm:grid-cols-2">
                <Input
                  label={`Received (${currency})`}
                  inputMode="decimal"
                  value={received}
                  onChange={(e) => setReceived(e.target.value)}
                  hint={!received && rateTyped && cash.minor > 0 ? `≈ ${formatHuman(cash)} at ${formatRateHuman(rateTyped)}` : undefined}
                  data-testid="match-received"
                />
                <Input
                  label={`Rate: 1 ${currency} = ${currencyMeta(deposit.currency).symbol}`}
                  inputMode="decimal"
                  value={receivedTyped.minor > 0 && impliedFx ? formatRateHuman(impliedFx) : typedRate}
                  onChange={(e) => setTypedRate(e.target.value)}
                  disabled={receivedTyped.minor > 0}
                  hint={receivedTyped.minor > 0 ? 'Derived from the deposit and what was received.' : undefined}
                  data-testid="match-rate"
                />
              </div>
            </div>
          ) : null}

          <div className="rounded-md border border-border p-3">
            <p className="text-sm font-medium">Deducted before you were paid</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              When the deposit is short of the invoice by what the customer withheld - TDS, or the bank&rsquo;s charge on the way in. Both
              settle the invoice; the money went somewhere else, it is not lost.
            </p>
            <div className="mt-3 grid gap-3 [&>*]:min-w-0 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-1.5 block font-medium">TDS section</span>
                <select
                  className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base sm:min-h-9 sm:text-sm"
                  value={tdsSection}
                  onChange={(e) => {
                    setTdsSection(e.target.value);
                    const section = findTdsSection(e.target.value);
                    if (section) setTdsRate(String(section.rate));
                  }}
                  data-testid="match-tds-section"
                >
                  <option value="">None</option>
                  {TDS_SECTIONS.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.code} — {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <Input label="Rate %" inputMode="decimal" value={tdsRate} onChange={(e) => setTdsRate(e.target.value)} hint={suggestedTds !== undefined ? `Usually ${suggestedTds}%` : undefined} />
            </div>
            <div className="mt-3 grid gap-3 [&>*]:min-w-0 sm:grid-cols-2">
              <Input
                label="TDS deducted"
                inputMode="decimal"
                value={tdsAmount}
                onChange={(e) => setTdsAmount(e.target.value)}
                hint="What they actually deducted."
                data-testid="match-tds-amount"
              />
              <Input label="Their TAN" value={tan} onChange={(e) => setTan(e.target.value.toUpperCase())} />
            </div>
            <div className="mt-3">
              <Input label="Bank charges" inputMode="decimal" value={bankCharges} onChange={(e) => setBankCharges(e.target.value)} data-testid="match-bank-charges" />
            </div>
          </div>

          {chosen ? (
            <dl className="space-y-1 rounded-md bg-muted/40 p-3 text-sm" data-testid="match-figures">
              <Row label={cross ? `Received (${currency})` : 'Deposit'} value={format(cash)} currency={currency} />
              {cross ? <Row label={`Credited (${deposit.currency})`} value={format(depositMoney)} currency={deposit.currency} /> : null}
              {withheld.minor > 0 ? <Row label="Withheld before payment" value={format(withheld)} currency={currency} /> : null}
              <Row label={`Applied to ${chosen.number}`} value={format(applied)} currency={currency} strong />
              <Row label="Balance after" value={format(balanceAfter)} currency={currency} />
              {credit.minor > 0 ? <Row label="Left as customer credit" value={format(credit)} currency={currency} /> : null}
            </dl>
          ) : null}
          {mostlyCredit ? (
            <p role="alert" className="rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200" data-testid="match-mostly-credit">
              Only <Money value={format(applied)} currency={currency} /> of this deposit would go to {chosen?.number}; the rest becomes credit on the
              customer&rsquo;s account. If this deposit paid a different invoice, pick that one instead.
            </p>
          ) : null}
          {tdsIncomplete ? (
            <p role="alert" className="text-sm text-destructive" data-testid="match-tds-section-missing">
              Say which section the TDS was deducted under - it is what the register and Form 26AS are reconciled on.
            </p>
          ) : null}

          {error ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter className="justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={settle.isPending} disabled={!invoiceId || tdsIncomplete || receivedMissing} data-testid="match-settle">
            Settle {chosen ? chosen.number : 'invoice'}
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

function Row({ label, value, currency, strong }: { label: string; value: string; currency: string; strong?: boolean }) {
  return (
    <div className={`flex items-center justify-between ${strong ? 'font-semibold' : ''}`}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>
        <Money value={value} currency={currency} />
      </dd>
    </div>
  );
}
